import React, { FC, ReactNode, useState, useEffect, useMemo, useRef } from 'react';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import identity from 'lodash/identity';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import InsetContainer from '@ad-scripts/presentation/components/common/containers/inset_container';
import PaginateLight from '@ad-scripts/presentation/components/common/paginate_light';
import { H3 } from '@ad-scripts/presentation/components/common/headers';

import { Button } from 'src/presentation/components/agent_desktop_components';
import Table from './Table';
import TableHeader from 'src/presentation/components/layouts/table/TableHeader';
import TableLayout from 'src/presentation/components/layouts/table/TableLayout';
import Tabs, { useTabFilter } from 'src/presentation/components/pages/shared/Tabs';
import Telescope from 'src/images/Telescope.svg';

import { InitialSorting } from './Table';

export type TabConfig = {
  columns?: Record<string, any>[];
  itemsKey?: string;
  key: string;
  label: string;
  renderRow?: Function;
  renderRowActions?: Function;
  searchFunc?: Function;
  showContentIfNoItems?: boolean;
};

export type PaginationConfig = { pageSize: number; itemsTitle: string };

export type TableListProps = {
  actions: any;
  backButtonUrl?: string;
  backButtonLabel?: string;
  className?: string;
  columnConfig?: any;
  dataAid: string;
  description?: any;
  disableSearch?: boolean;
  dragEndHandler?: Function;
  emptyFilterImage?: any;
  emptyFilterState?: string | ReactNode;
  emptyStateFullHeightLayout?: boolean;
  emptyStateHideTableHeader?: boolean;
  emptyStateRender?: string | ReactNode;
  filterFunc?: Function;
  filterPlaceholder?: string;
  initialSearchString?: string;
  initialSorting?: InitialSorting;
  isLoading: boolean;
  items: any;
  onTabChange?: Function;
  pageName: string;
  pagination?: PaginationConfig;
  preselectedTab?: string;
  renderRow?: Function;
  renderRowActions?: Function;
  root?: string;
  leftBottomHeaderSlot?: JSX.Element;
  tabConfig?: TabConfig[];
  updateUrlOnTabChange?: boolean;
};

const TableList: FC<TableListProps> = props => {
  const {
    actions,
    backButtonUrl,
    backButtonLabel,
    className,
    columnConfig,
    dataAid,
    description,
    disableSearch,
    dragEndHandler,
    emptyFilterImage,
    emptyFilterState,
    emptyStateFullHeightLayout,
    emptyStateHideTableHeader,
    emptyStateRender,
    filterFunc,
    filterPlaceholder = 'Search',
    initialSearchString = '',
    initialSorting,
    isLoading,
    items,
    onTabChange,
    pageName,
    pagination,
    preselectedTab,
    renderRow,
    renderRowActions,
    leftBottomHeaderSlot,
    tabConfig,
    root,
    updateUrlOnTabChange,
  } = props;
  const [tabsProps] = useTabFilter({ tabConfig, items, onTabChange, preselectedTab, updateUrlOnTabChange, root });
  const [searchString, setSearchString] = useState(initialSearchString);
  const [currentPage, updatePage] = useState(1);
  const [pageSize, updatePageSize] = useState(pagination?.pageSize || 50);
  const [searchInputRef, setSearchInputRef] = useState<any>();
  const searchRef = useRef<any>();
  const currentTabTitle = tabsProps.selectedTabLabel;
  const currentTab = find(tabConfig, ['label', currentTabTitle]);
  const searchPlaceholder = get(currentTab, 'filterPlaceholder', filterPlaceholder);

  const resetPagination = () => {
    updatePage(1);
  };

  const tabItems = useMemo(() => {
    const itemsKey = get(currentTab, 'itemsKey', '');

    const entries = get(items, itemsKey, items);
    const predicate = get(currentTab, 'filter');

    return filter(entries, predicate);
  }, [currentTab, items]);

  const tabColumns = get(currentTab, 'columns', columnConfig);
  const tabSearchFunc = get(currentTab, 'searchFunc', filterFunc) || identity;
  const tabRenderRow = get(currentTab, 'renderRow', renderRow);
  const tabRenderRowActions = get(currentTab, 'renderRowActions', renderRowActions);

  const tabs = tabConfig ? <Tabs dataAid={dataAid} {...tabsProps} /> : null;
  const filteredItems = useMemo(
    () => tabSearchFunc(tabItems, searchString, currentTabTitle),
    [tabItems, tabSearchFunc, searchString, currentTabTitle]
  );
  useEffect(resetPagination, [searchString, pageSize, filteredItems, tabItems]);

  const handleClearFilterTexts = () => {
    setSearchString('');
    searchInputRef?.focus();
    searchRef.current.clearValue();
  };

  const emptyFilterSrc = emptyFilterImage || Telescope;
  const emptyFilterStateContent = (
    <EmptyStateWrapper data-aid={`${dataAid}-emptyMessage`}>
      {emptyFilterState || (
        <>
          <img src={emptyFilterSrc} />
          <StyledH3>Whoopsies - nothing found here.</StyledH3>
          <P>Looks like there&apos;s no match for your search. Check for typos or try a different search term.</P>
          <Button data-aid={`${dataAid}-clear-search`} onClick={handleClearFilterTexts}>
            Search again
          </Button>
        </>
      )}
    </EmptyStateWrapper>
  );

  // empty state should also depend on `isLoading` to avoid unnecessary rendering of the empty state
  // before the loading spinner appears
  const emptyState =
    !searchString && !isLoading && isEmpty(filteredItems) && emptyStateRender ? emptyStateRender : null;

  const paginationConfig = pagination && { ...pagination, pageSize };
  const emptyFilterList = searchString && filteredItems.length === 0;
  const tableWrapper = (
    <>
      <Table
        dataAid={dataAid}
        columnConfig={tabColumns}
        items={filteredItems}
        dragEndHandler={dragEndHandler}
        renderRowActions={tabRenderRowActions}
        tabRenderRow={tabRenderRow}
        isLoading={isLoading}
        initialSorting={initialSorting}
        pagination={paginationConfig}
        currentPage={currentPage}
        onChangeSorting={resetPagination}
      />
      {emptyFilterList && !isLoading ? emptyFilterStateContent : null}
      {emptyState}
    </>
  );

  const showFullHeightLayout = emptyStateFullHeightLayout && (!!emptyState || emptyFilterList);

  return (
    <TableLayoutHeightRestrictor className={className} showFullHeightLayout={showFullHeightLayout}>
      <Flexer showFullHeightLayout={showFullHeightLayout}>
        <TableLayout data-aid={dataAid} showFullHeightLayout={showFullHeightLayout}>
          <TableHeader
            actionButtons={actions}
            backButtonUrl={backButtonUrl}
            backButtonLabel={backButtonLabel}
            currentPage={currentPage}
            dataAid={dataAid}
            description={description}
            disableSearch={disableSearch}
            header={pageName}
            items={filteredItems}
            setInputRef={setSearchInputRef}
            setSearchRef={searchRef}
            onChangeSearchFilter={setSearchString}
            searchPlaceholder={searchPlaceholder}
            searchString={searchString}
            pageSize={pageSize}
            paginationItemsTitle={pagination?.itemsTitle}
            showPagination={Boolean(pagination)}
            leftBottomSlot={leftBottomHeaderSlot}
            updatePage={updatePage}
          />
          {tabs}
          {emptyStateHideTableHeader && emptyState ? emptyState : tableWrapper}
        </TableLayout>
      </Flexer>
      <PaginateStyled
        currentPage={currentPage}
        list={filteredItems}
        onChange={updatePage}
        pageSize={pageSize}
        showPagination={Boolean(pagination)}
        showSizeChanger={true}
        title={pagination?.itemsTitle}
        onChangePageSize={updatePageSize}
      />
    </TableLayoutHeightRestrictor>
  );
};

const Flexer = styled.div`
  display: flex;
  ${({ showFullHeightLayout }) => (showFullHeightLayout ? 'height: 100%' : '')};
`;

const TableLayoutHeightRestrictor = styled.div`
  flex-grow: 1;

  ${({ showFullHeightLayout }) => (showFullHeightLayout ? 'height: calc(100vh - 60px)' : '')};
  overflow: auto;
`;

const PaginateStyled = styled(PaginateLight)`
  margin-bottom: 20px;
  margin-right: 40px;
`;

const P = styled.p`
  width: 375px;
  text-align: center;
  margin-bottom: 16px;
  line-height: 21px;
`;

const StyledH3 = styled(H3)`
  margin: 24px 0 0 0;
`;

export const EmptyStateWrapper = styled(InsetContainer).attrs({ inset: 'large' })`
  flex: 1;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export default TableList;
